import "@rmlibrary/fetch";

import { interpolateValue } from "./utilities";

const annualNumberOfMonths = 12;
const assetAllocations = [
    { fixedIncome: 100, canadianEquities: 0, foreignEquities: 0 },
    { fixedIncome: 80, canadianEquities: 10, foreignEquities: 10 },
    { fixedIncome: 60, canadianEquities: 20, foreignEquities: 20 },
    { fixedIncome: 50, canadianEquities: 25, foreignEquities: 25 },
    { fixedIncome: 40, canadianEquities: 30, foreignEquities: 30 },
    { fixedIncome: 20, canadianEquities: 40, foreignEquities: 40 },
    { fixedIncome: 0, canadianEquities: 50, foreignEquities: 50 }
];

let data = null;

function getData() {
    if (data) {
        return Promise.resolve(data);
    }

    return fetch("./common/data/data.json")
        .then((response) => response.json())
        .then((json) => {
            data = json;
            return data;
        });
}

function calculateMonthlyRateOfReturns({ assetMix, indices }) {
    const { fixedIncome, canadianEquities, foreignEquities } = assetAllocations[assetMix];

    return indices.map(({ date, fixedIncomeRate, canadianEquitiesRate, foreignEquitiesRate }) => ({
        date,
        rateOfReturn:
            ((fixedIncome / 100) * fixedIncomeRate +
                (canadianEquities / 100) * canadianEquitiesRate +
                (foreignEquities / 100) * foreignEquitiesRate +
                100) /
            100
    }));
}

function calculateProduct(values) {
    return values.reduce((result, value) => result * value, 1);
}

function calculateRollingRateOfReturns({ endDate, monthlyRateOfReturns, startDate, timePeriod }) {
    const startIndex = monthlyRateOfReturns.findIndex(({ date }) => date === startDate);
    const endIndex = monthlyRateOfReturns.findIndex(({ date }) => date === endDate);

    let rollingRateOfReturns = [];

    for (let currentIndex = startIndex; currentIndex <= endIndex; currentIndex++) {
        const { date } = monthlyRateOfReturns[currentIndex];
        const monthlyRateOfReturnsStartIndex = currentIndex - timePeriod * annualNumberOfMonths + 1;
        const monthlyRateOfReturnsEndIndex = currentIndex;
        const rollingRateOfReturnsForTimePeriod = monthlyRateOfReturns
            .slice(monthlyRateOfReturnsStartIndex, monthlyRateOfReturnsEndIndex + 1)
            .map(({ rateOfReturn }) => rateOfReturn);
        const rollingRateOfReturn = calculateProduct(rollingRateOfReturnsForTimePeriod) ** (1 / timePeriod) - 1;

        rollingRateOfReturns = rollingRateOfReturns.concat({
            date,
            rateOfReturn: rollingRateOfReturn
        });
    }

    return rollingRateOfReturns;
}

function calculateInvestmentGrowthPortfolioValues({ bestAndWorstYears, investmentAmount, rateOfReturns }) {
    let portfolioValues = [];

    const {
        bestYear: { date: bestYearDate, rateOfReturn: bestYearRateOfReturn },
        worstYear: { date: worstYearDate, rateOfReturn: worstYearRateOfReturn }
    } = bestAndWorstYears;

    portfolioValues = rateOfReturns.reduce((results, { date, rateOfReturn }, index) => {
        const previousPortfolioValue = index < 1 ? investmentAmount : results.slice(-1)[0].portfolioValue;
        const result = {
            date,
            portfolioValue: rateOfReturn * previousPortfolioValue
        };

        if (date === bestYearDate) {
            result.bestYear = true;
            result.rateOfReturn = bestYearRateOfReturn;
        }

        if (date === worstYearDate) {
            result.worstYear = true;
            result.rateOfReturn = worstYearRateOfReturn;
        }

        if (index + 1 === rateOfReturns.length) {
            result.last = true;
        }

        return results.concat(result);
    }, portfolioValues);

    return portfolioValues;
}

function calculateTimeToRecoverPortfolioValues({ investmentAmount, rateOfReturns, timePeriod }) {
    let portfolioValues = [];

    portfolioValues = [{ month: 0, portfolioValue: investmentAmount }];
    let hasRecoveryMonth = false;

    const startMonth = 1;
    const endMonth = timePeriod * 12 + 3;
    for (let i = startMonth; i < endMonth + 1; i++) {
        const previousPortfolioValue = portfolioValues.slice(-1)[0].portfolioValue;
        let rateOfReturn = rateOfReturns.threeMonths;
        if (i > 3 && i <= 15) {
            rateOfReturn = rateOfReturns.oneYear;
        } else if (i > 15 && i <= 39) {
            rateOfReturn = rateOfReturns.threeYear;
        } else if (i > 39) {
            rateOfReturn = rateOfReturns.fiveYear;
        }

        const dataPoint = {
            month: i,
            portfolioValue: (rateOfReturn + 1) * previousPortfolioValue
        };

        if (i > 3 && !hasRecoveryMonth && dataPoint.portfolioValue > investmentAmount) {
            const recoveryMonth = interpolateValue({
                x1: previousPortfolioValue,
                y1: dataPoint.month - 1,
                x2: dataPoint.portfolioValue,
                y2: dataPoint.month,
                x: investmentAmount
            });

            if (recoveryMonth < dataPoint.month) {
                portfolioValues = portfolioValues.concat({
                    month: recoveryMonth,
                    portfolioValue: investmentAmount,
                    recoveryMonth: true
                });
            } else {
                dataPoint.recoveryMonth = true;
            }

            hasRecoveryMonth = true;
        }

        if (i === endMonth) {
            dataPoint.last = true;
        }

        portfolioValues = portfolioValues.concat(dataPoint);
    }
    return portfolioValues;
}

function calculateBestAndWorstYears({ investmentAmount, rollingRateOfReturns, timePeriod }) {
    const negativeReturnsCount = rollingRateOfReturns.reduce(
        (result, { rateOfReturn }) => (rateOfReturn < 0 ? result + 1 : result),
        0
    );
    const positiveReturnsCount = rollingRateOfReturns.reduce(
        (result, { rateOfReturn }) => (rateOfReturn >= 0 ? result + 1 : result),
        0
    );

    const negativeReturnsPercentage = (negativeReturnsCount / (negativeReturnsCount + positiveReturnsCount)) * 100;
    const positiveReturnsPercentage = (positiveReturnsCount / (negativeReturnsCount + positiveReturnsCount)) * 100;

    const { date: bestYearDate, rateOfReturn: bestYearRateOfReturn } = rollingRateOfReturns.reduce(
        (best, current) => (best.rateOfReturn > current.rateOfReturn ? best : current),
        rollingRateOfReturns[0]
    );
    const { date: worstYearDate, rateOfReturn: worstYearRateOfReturn } = rollingRateOfReturns.reduce(
        (worst, current) => (worst.rateOfReturn < current.rateOfReturn ? worst : current),
        rollingRateOfReturns[0]
    );

    const bestYearPortfolioValue = (bestYearRateOfReturn + 1) ** timePeriod * investmentAmount;
    const worstYearPortfolioValue = (worstYearRateOfReturn + 1) ** timePeriod * investmentAmount;

    return {
        bestYear: {
            date: bestYearDate,
            portfolioValue: bestYearPortfolioValue,
            rateOfReturn: bestYearRateOfReturn
        },
        worstYear: {
            date: worstYearDate,
            portfolioValue: worstYearPortfolioValue,
            rateOfReturn: worstYearRateOfReturn
        },
        negativeReturns: {
            count: negativeReturnsCount,
            percentage: negativeReturnsPercentage
        },
        positiveReturns: {
            count: positiveReturnsCount,
            percentage: positiveReturnsPercentage
        }
    };
}

function calculateLinearRateOfReturns({ assetMix, rateOfReturns }) {
    return rateOfReturns[assetMix];
}

function calculateAssetMixResults({ assetMix, data: { indices, rateOfReturns }, investmentAmount, timePeriods }) {
    const endDate = "12/2025";

    const monthlyRateOfReturns = calculateMonthlyRateOfReturns({ assetMix, indices });
    let startDate = monthlyRateOfReturns[annualNumberOfMonths - 1].date;
    const oneYearRollingRateOfReturns = calculateRollingRateOfReturns({
        endDate,
        monthlyRateOfReturns,
        startDate,
        timePeriod: 1
    });
    const oneYearBestAndWorstYears = calculateBestAndWorstYears({
        investmentAmount,
        rollingRateOfReturns: oneYearRollingRateOfReturns,
        timePeriod: 1
    });
    const investmentGrowthPortfolioValues = calculateInvestmentGrowthPortfolioValues({
        bestAndWorstYears: oneYearBestAndWorstYears,
        investmentAmount,
        rateOfReturns: monthlyRateOfReturns,
        type: "investmentGrowth"
    });

    startDate = monthlyRateOfReturns[timePeriods.bestAndWorst * annualNumberOfMonths - 1].date;
    const rollingRateOfReturns = calculateRollingRateOfReturns({
        endDate,
        monthlyRateOfReturns,
        startDate,
        timePeriod: timePeriods.bestAndWorst
    });
    const bestAndWorstYears = calculateBestAndWorstYears({
        investmentAmount,
        rollingRateOfReturns,
        timePeriod: timePeriods.bestAndWorst
    });

    const linearRateOfReturns = calculateLinearRateOfReturns({ assetMix, rateOfReturns });
    const timeToRecoverPortfolioValues = calculateTimeToRecoverPortfolioValues({
        investmentAmount,
        rateOfReturns: linearRateOfReturns,
        timePeriod: timePeriods.timeToRecover
    });

    return {
        investmentGrowth: investmentGrowthPortfolioValues,
        bestAndWorst: bestAndWorstYears,
        timeToRecover: timeToRecoverPortfolioValues
    };
}

function calculateResults({ assetMixes, ...otherInputs }) {
    return getData().then((data) => {
        const [assetMixOne, assetMixTwo] = assetMixes;
        let assetMixResults = calculateAssetMixResults({ ...otherInputs, assetMix: assetMixOne, data });
        let results = {
            investmentGrowth: {
                assetMixOne: assetMixResults.investmentGrowth
            },
            bestAndWorst: {
                assetMixOne: assetMixResults.bestAndWorst
            },
            timeToRecover: {
                assetMixOne: assetMixResults.timeToRecover
            }
        };

        if (assetMixTwo !== undefined) {
            assetMixResults = calculateAssetMixResults({ ...otherInputs, assetMix: assetMixTwo, data });
            results = {
                investmentGrowth: {
                    ...results.investmentGrowth,
                    assetMixTwo: assetMixResults.investmentGrowth
                },
                bestAndWorst: {
                    ...results.bestAndWorst,
                    assetMixTwo: assetMixResults.bestAndWorst
                },
                timeToRecover: {
                    ...results.timeToRecover,
                    assetMixTwo: assetMixResults.timeToRecover
                }
            };
        }

        return results;
    });
}

export { calculateResults };
